import {BottomSheet} from '@components/ui/bottom-sheet/BottomSheet.tsx'
import {StyledModalBody} from '@components/ui/bottom-sheet/style.ts'
import {Flexbox} from '@components/ui/flexbox/FlexBox.tsx'
import {StyledContainerChipMobile, StyledFilterIcon} from '@components/ui/measure-filters/style.ts'
import {Chip} from '@components/ui/chip/Chip.tsx'
import {Button} from '@components/ui/button/Button.tsx'
import {FilterFieldNames, FilterFormValues, useFilterMeasures} from '@hooks/useFilterMeasures.tsx'
import {useTranslation} from 'react-i18next'
import {useTheme} from 'styled-components'
import {FC, ReactNode, useState} from 'react'
import {MultiSelectBase} from '../multi-select/MultiSelectBase'
import {MultiSelect} from '@components/ui/multi-select/MultiSelect.tsx'
import {TextInputFilter} from '@components/ui/text-input-filter/TextInputFilter.tsx'

type MeasureFiltersMobileProps = {
    filterFields: Record<
        Exclude<FilterFieldNames, 'activeTab' | 'page' | 'perPage'>,
        {
            label: string
            icon: ReactNode
            options: {id: string; name: string}[]
            isFetchingNextPage?: boolean
            fetchNextPage?: () => void
            hasNextPage?: boolean
            setSearchSections?: (value: string) => void
            isLoading?: boolean
        }
    >
    debouncedSubmit: (data: Partial<FilterFormValues>) => void
    filterCounter: number
}

export const MeasureFiltersMobile: FC<MeasureFiltersMobileProps> = ({filterFields, debouncedSubmit, filterCounter}) => {
    const {t} = useTranslation()
    const theme = useTheme()
    const {resetFilters, filters} = useFilterMeasures()

    const [tempFilters, setTempFilters] = useState<FilterFormValues>(filters)
    const [isModalOpen, setModalOpen] = useState(false)
    const [originalFilters, setOriginalFilters] = useState<FilterFormValues>(filters)

    const openModal = () => {
        setTempFilters(filters)
        setOriginalFilters(filters)
        setModalOpen(true)
    }

    const closeModalWithoutApplying = () => {
        setTempFilters(originalFilters)
        setModalOpen(false)
    }

    const applyFilters = () => {
        const {general_number, ...restFilters} = tempFilters
        debouncedSubmit({
            ...restFilters,
            general_number
        })
        setModalOpen(false)
    }

    const handleResetFilters = () => {
        const emptyFilters: FilterFormValues = {
            type_of_measure: [],
            general_role: [],
            site: [],
            counter_part: [],
            section: [],
            panel: [],
            activeTab: 'all',
            general_number: '',
            perPage: 25,
            page: 1
        }
        setTempFilters(emptyFilters)
        resetFilters()
        setModalOpen(false)
    }

    return (
        <>
            <Button onClick={openModal} variant={filterCounter > 0 ? 'primary' : 'secondary'}>
                {filterCounter > 0 && <p>{`(${filterCounter})`}</p>}
                <StyledFilterIcon fill={'none'} $filterLength={filterCounter} />
            </Button>

            <BottomSheet isOpen={isModalOpen} onClose={closeModalWithoutApplying} title={t('measures:filters:filters')}>
                <StyledModalBody direction="column" gap={4} justify={'space-between'}>
                    <Flexbox direction="column" gap={4} fullWidth>
                        <Flexbox direction={'column'} fullWidth>
                            <MultiSelect
                                value={tempFilters.counter_part}
                                options={filterFields.counter_part.options}
                                displayLabel={t(filterFields.counter_part.label)}
                                displayIcon={filterFields.counter_part.icon}
                                searchPlaceholder={t('measures:filters:select_counter_parts')}
                                onChange={selectedValues => {
                                    setTempFilters(prev => ({
                                        ...prev,
                                        counter_part: selectedValues
                                    }))
                                }}
                                isFetchingNextPage={filterFields.counter_part.isFetchingNextPage}
                                fetchNextPage={filterFields.counter_part.fetchNextPage}
                                hasNextPage={filterFields.counter_part.hasNextPage}
                                setSearchSections={filterFields.counter_part.setSearchSections}
                                sameWidth
                            />
                            {tempFilters.counter_part.length > 0 && (
                                <StyledContainerChipMobile direction={'row'} gap={2} fullWidth>
                                    {tempFilters.counter_part.map(value => {
                                        const field = filterFields.counter_part
                                        const option = field?.options?.find(option => option.id === value.id)
                                        return (
                                            <Chip
                                                key={value.id}
                                                textColor={theme.palette.neutral[700]}
                                                bgColor={theme.palette.neutral[100]}
                                                text={option?.name || value.name}
                                                onDelete={() => {
                                                    setTempFilters(prev => ({
                                                        ...prev,
                                                        counter_part: prev.counter_part.filter(v => v.id !== value.id)
                                                    }))
                                                }}
                                            />
                                        )
                                    })}
                                </StyledContainerChipMobile>
                            )}
                        </Flexbox>

                        <Flexbox direction={'column'} fullWidth>
                            <MultiSelectBase
                                value={tempFilters.type_of_measure}
                                options={filterFields.type_of_measure.options}
                                displayLabel={t(filterFields.type_of_measure.label)}
                                displayIcon={filterFields.type_of_measure.icon}
                                searchPlaceholder={t('measures:filters:select_type_of_measures')}
                                onChange={selectedValues => {
                                    setTempFilters(prev => ({
                                        ...prev,
                                        type_of_measure: selectedValues
                                    }))
                                }}
                                sameWidth
                            />
                            {tempFilters.type_of_measure.length > 0 && (
                                <StyledContainerChipMobile direction={'row'} gap={2} fullWidth>
                                    {tempFilters.type_of_measure.map(value => {
                                        const field = filterFields.type_of_measure
                                        const option = field?.options?.find(option => option.id === value.id)
                                        return (
                                            <Chip
                                                key={value.id}
                                                textColor={theme.palette.neutral[700]}
                                                bgColor={theme.palette.neutral[100]}
                                                text={option?.name || value.name}
                                                onDelete={() => {
                                                    setTempFilters(prev => ({
                                                        ...prev,
                                                        type_of_measure: prev.type_of_measure.filter(
                                                            v => v.id !== value.id
                                                        )
                                                    }))
                                                }}
                                            />
                                        )
                                    })}
                                </StyledContainerChipMobile>
                            )}
                        </Flexbox>

                        <Flexbox direction={'column'} fullWidth>
                            <MultiSelectBase
                                value={tempFilters.general_role}
                                options={filterFields.general_role.options}
                                displayLabel={t(filterFields.general_role.label)}
                                displayIcon={filterFields.general_role.icon}
                                searchPlaceholder={t('measures:filters:general_role')}
                                onChange={selectedValues => {
                                    setTempFilters(prev => ({
                                        ...prev,
                                        general_role: selectedValues
                                    }))
                                }}
                                sameWidth
                            />
                            {tempFilters.general_role.length > 0 && (
                                <StyledContainerChipMobile direction={'row'} gap={2} fullWidth>
                                    {tempFilters.general_role.map(value => {
                                        const field = filterFields.general_role
                                        const option = field?.options?.find(option => option.id === value.id)
                                        return (
                                            <Chip
                                                key={value.id}
                                                textColor={theme.palette.neutral[700]}
                                                bgColor={theme.palette.neutral[100]}
                                                text={option?.name || value.name}
                                                onDelete={() => {
                                                    setTempFilters(prev => ({
                                                        ...prev,
                                                        general_role: prev.general_role.filter(v => v.id !== value.id)
                                                    }))
                                                }}
                                            />
                                        )
                                    })}
                                </StyledContainerChipMobile>
                            )}
                        </Flexbox>

                        <Flexbox direction={'column'} fullWidth>
                            <TextInputFilter
                                value={tempFilters.general_number}
                                onChange={selectedValue => {
                                    setTempFilters(prev => ({
                                        ...prev,
                                        general_number: selectedValue
                                    }))
                                }}
                                displayLabel={t(filterFields.general_number.label)}
                                displayIcon={filterFields.general_number.icon}
                                placeholder={t('measures:filters:general_number')}
                                sameWidth={true}
                                multiple={false}
                            />
                            {tempFilters.general_number && (
                                <StyledContainerChipMobile direction={'row'} gap={2} fullWidth>
                                    <Chip
                                        textColor={theme.palette.neutral[700]}
                                        bgColor={theme.palette.neutral[100]}
                                        text={`${tempFilters.general_number}`}
                                        onDelete={() => {
                                            setTempFilters(prev => ({
                                                ...prev,
                                                general_number: ''
                                            }))
                                        }}
                                    />
                                </StyledContainerChipMobile>
                            )}
                        </Flexbox>

                        <Flexbox direction={'column'} fullWidth>
                            <MultiSelect
                                value={tempFilters.site}
                                options={filterFields.site.options}
                                displayLabel={t(filterFields.site.label)}
                                displayIcon={filterFields.site.icon}
                                searchPlaceholder={t('measures:filters:select_sites')}
                                onChange={selectedValues => {
                                    setTempFilters(prev => ({
                                        ...prev,
                                        site: selectedValues
                                    }))
                                }}
                                isFetchingNextPage={filterFields.site.isFetchingNextPage}
                                fetchNextPage={filterFields.site.fetchNextPage}
                                hasNextPage={filterFields.site.hasNextPage}
                                setSearchSections={filterFields.site.setSearchSections}
                                sameWidth
                            />
                            {tempFilters.site.length > 0 && (
                                <StyledContainerChipMobile direction={'row'} gap={2} fullWidth>
                                    {tempFilters.site.map(value => {
                                        const field = filterFields.site
                                        const option = field?.options?.find(option => option.id === value.id)
                                        return (
                                            <Chip
                                                key={value.id}
                                                textColor={theme.palette.neutral[700]}
                                                bgColor={theme.palette.neutral[100]}
                                                text={option?.name || value.name}
                                                onDelete={() => {
                                                    setTempFilters(prev => ({
                                                        ...prev,
                                                        site: prev.site.filter(v => v.id !== value.id)
                                                    }))
                                                }}
                                            />
                                        )
                                    })}
                                </StyledContainerChipMobile>
                            )}
                        </Flexbox>

                        <Flexbox direction={'column'} fullWidth>
                            <MultiSelect
                                value={tempFilters.section}
                                options={filterFields.section.options}
                                displayLabel={t(filterFields.section.label)}
                                displayIcon={filterFields.section.icon}
                                searchPlaceholder={t('measures:filters:select_sections')}
                                onChange={selectedValues => {
                                    setTempFilters(prev => ({
                                        ...prev,
                                        section: selectedValues
                                    }))
                                }}
                                isFetchingNextPage={filterFields.section.isFetchingNextPage}
                                fetchNextPage={filterFields.section.fetchNextPage}
                                hasNextPage={filterFields.section.hasNextPage}
                                setSearchSections={filterFields.section.setSearchSections}
                                sameWidth
                            />
                            {tempFilters.section.length > 0 && (
                                <StyledContainerChipMobile direction={'row'} gap={2} fullWidth>
                                    {tempFilters.section.map(value => {
                                        const field = filterFields.section
                                        const option = field?.options?.find(option => option.id === value.id)
                                        return (
                                            <Chip
                                                key={value.id}
                                                textColor={theme.palette.neutral[700]}
                                                bgColor={theme.palette.neutral[100]}
                                                text={option?.name || value.name}
                                                onDelete={() => {
                                                    setTempFilters(prev => ({
                                                        ...prev,
                                                        section: prev.section.filter(v => v.id !== value.id)
                                                    }))
                                                }}
                                            />
                                        )
                                    })}
                                </StyledContainerChipMobile>
                            )}
                        </Flexbox>

                        <Flexbox direction={'column'} fullWidth>
                            <MultiSelect
                                value={tempFilters.panel}
                                options={filterFields.panel.options}
                                displayLabel={t(filterFields.panel.label)}
                                displayIcon={filterFields.panel.icon}
                                searchPlaceholder={t('measures:filters:select_panels')}
                                onChange={selectedValues => {
                                    setTempFilters(prev => ({
                                        ...prev,
                                        panel: selectedValues
                                    }))
                                }}
                                isFetchingNextPage={filterFields.panel.isFetchingNextPage}
                                fetchNextPage={filterFields.panel.fetchNextPage}
                                hasNextPage={filterFields.panel.hasNextPage}
                                setSearchSections={filterFields.panel.setSearchSections}
                                sameWidth
                            />
                            {tempFilters.panel.length > 0 && (
                                <StyledContainerChipMobile direction={'row'} gap={2} fullWidth>
                                    {tempFilters.panel.map(value => {
                                        const field = filterFields.panel
                                        const option = field?.options?.find(option => option.id === value.id)
                                        return (
                                            <Chip
                                                key={`${value.id}-${option?.name}`}
                                                textColor={theme.palette.neutral[700]}
                                                bgColor={theme.palette.neutral[100]}
                                                text={`${option?.name || value.name}`}
                                                onDelete={() => {
                                                    setTempFilters(prev => ({
                                                        ...prev,
                                                        panel: prev.panel.filter(v => v.id !== value.id)
                                                    }))
                                                }}
                                            />
                                        )
                                    })}
                                </StyledContainerChipMobile>
                            )}
                        </Flexbox>
                    </Flexbox>

                    <Flexbox direction="column" gap={2} fullWidth>
                        <Button fullWidth variant="primaryGhost" onClick={handleResetFilters}>
                            {t('measures:filters:reset_filters')}
                        </Button>
                        <Button fullWidth type="submit" onClick={applyFilters}>
                            {t('measures:filters:apply_filters')}
                        </Button>
                    </Flexbox>
                </StyledModalBody>
            </BottomSheet>
        </>
    )
}
